/**
 * Vuetify configuration.
 *
 * GRANDDesignerのデザイントークン (tokens.css) と整合する独自テーマ "grandDark" を定義。
 * Vuetifyコンポーネントのprimary/background/surface/error/successは tokens.css と同じ値を使う。
 */
import { createVuetify } from 'vuetify';
import type { ThemeDefinition } from 'vuetify';
import 'vuetify/styles';
import '@mdi/font/css/materialdesignicons.css';

/**
 * tokens.css (--ds-*) との対応表。値は常に一致させること
 * (src/plugins/vuetify-theme.test.ts がテキスト照合で検証する)。
 * ビルド時の単一源生成はスコープ外のため、二重管理をコメント+テストで固定する。
 */
const grandDark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#121212', // --ds-bg-primary
    surface: '#1e1e1e', // --ds-bg-secondary
    'surface-bright': '#2a2a2a', // --ds-bg-tertiary / --ds-bg-elevated
    'surface-variant': '#333333', // --ds-bg-hover
    'on-surface': '#e8e8e8', // --ds-text-primary
    'on-surface-variant': '#999999', // --ds-text-secondary

    primary: '#15a05c', // --ds-accent-primary
    'primary-darken-1': '#128a4f', // --ds-accent-primary-hover
    'on-primary': '#ffffff', // (tokens.css 対応なし)

    secondary: '#0d9488', // --ds-accent-gradient の第2色
    'on-secondary': '#ffffff', // (tokens.css 対応なし)

    success: '#34a853', // --ds-success
    warning: '#fbbc04', // --ds-warning
    error: '#ea4335', // --ds-error
    info: '#4285f4', // --ds-info
  },
  variables: {
    'border-color': '#ffffff',
    'border-opacity': 0.1,
    'hover-opacity': 0.06,
    'focus-opacity': 0.12,
    'selected-opacity': 0.12,
    'activated-opacity': 0.16,
    'pressed-opacity': 0.2,
    'dragged-opacity': 0.08,
    'theme-kbd': '#212529',
    'theme-on-kbd': '#ffffff',
    'theme-code': '#f5f5f5',
    'theme-on-code': '#000000',
  },
};

/**
 * 設置場所マップだけアクセントを青にする。地図の主役がピン (既定 #1d58ae) なので
 * UI もその色で揃える。他の3マップは緑 — あちらは地図が赤/橙/紺/緑を使っており、
 * 空いている色が違う。
 *
 * ピンの #1d58ae そのままだとサイドバー地 (#1e1e1e) に対して 2.4:1 しかなく、
 * トグルやスライダーの塗りが沈む。同系で明度を上げた値を使う
 * (背景に対し 4.4:1 / 白文字 3.8:1。緑の 4.9:1 / 3.4:1 とほぼ同じ見え方)。
 */
const SETUP_MAP_ACCENT = '#3b82e8';
const SETUP_MAP_ACCENT_HOVER = '#2f77d6';

const setupMapDark: ThemeDefinition = {
  ...grandDark,
  colors: {
    ...grandDark.colors,
    primary: SETUP_MAP_ACCENT,
    'primary-darken-1': SETUP_MAP_ACCENT_HOVER,
  },
};

export const vuetify = createVuetify({
  theme: {
    defaultTheme: 'grandDark',
    themes: {
      grandDark,
      setupMapDark,
    },
  },
  defaults: {
    VBtn: {
      // 角丸を tokens の --ds-radius-md (8px) に合わせる
      rounded: 'md',
      // Material Designのデフォルトは大文字化されるが、日本語UIではoffに
      style: 'text-transform: none; letter-spacing: 0;',
    },
    VSwitch: {
      /* トグルの見た目は1箇所で決める。画面ごとに inset を付けたり付けなかったり
         していて、設置場所マップだけ形が違っていた。
         inset は付けない — 軌道が 52x32 と大きく、サイドバーの1行に収まらない
         (既定は 36x14)。密度も既定で compact。 */
      color: 'primary',
      density: 'compact',
    },
    VTextField: {
      variant: 'outlined',
      density: 'comfortable',
    },
    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
    },
    VCard: {
      rounded: 'lg',
    },
  },
});
